/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RelatedDataSection — Accordion: picks which related tables a custom record
 * layout may pull data from.
 *
 * Arcee already knows how tables connect: every edge here is read from the FK
 * metadata by the related-edges endpoint, so the builder ticks and names rather
 * than typing table or column names. That also means the panel works unchanged
 * for any application's schema — nothing about the fitness tables is assumed.
 *
 * TWO RULES THIS UI EXISTS TO MAKE VISIBLE:
 *
 * 1. A table that is not itself public cannot be pulled onto a public page.
 *    Those edges are shown DISABLED with the reason rather than omitted — a
 *    missing option looks like a bug, a disabled one with a reason explains.
 *
 * 2. Column exposure is fail-closed. Ticking an edge exposes NOTHING until
 *    columns are chosen, because the server's sanitiser defaults to exposing
 *    every column, which is right for a table someone published and wrong for
 *    a related table nobody did. A ticked edge with no columns is therefore
 *    flagged as incomplete rather than quietly shipping an empty block.
 *
 * Nesting (a child of a child) is edited by NestedRelatedBlocks under each
 * ticked table, when the page supplies `loadChildEdges`. Without it the panel
 * edits one level only and deeper trees remain configurable through the API —
 * which is how it behaved before nesting was added here.
 */

import React from 'react';
import NestedRelatedBlocks, { withChildren } from './NestedRelatedBlocks';

function edgesOf(config) {
  return config?.related_config?.children || [];
}

function sourcesOf(config) {
  return config?.related_config?.sources || [];
}

export default function RelatedDataSection({
  config, updateConfig, theme, edges, tableName, tables, ownFields,
  // Optional: (tableName) => Promise<edges>. When given, each ticked table
  // offers its own related tables beneath it (see NestedRelatedBlocks). When
  // absent the panel is exactly the one-level panel it always was.
  loadChildEdges,
}) {
  const available = edges || [];
  const selected = edgesOf(config);
  const byTable = Object.fromEntries(selected.map(e => [e.table, e]));

  // Column-matched sources are kept in their own list. Relationship edges keep
  // writing to `children` exactly as before, so an existing configuration is
  // never rewritten just because this panel gained a second kind of source.
  const sources = sourcesOf(config);
  const candidates = tables || [];
  const recordFields = ownFields || [];

  // This page does not always resolve the table's own name — its header falls
  // back to "Table <id>" when the metadata is missing. Without a fallback here
  // the copy reads "for a table with no relationship to ." with a dangling
  // preposition, which looks like a rendering bug rather than missing data.
  const subject = tableName || 'this table';

  // Conditions being edited, per source. Cleared for a source whenever its
  // table changes, since the old columns mean nothing on the new table.
  const [drafts, setDrafts] = React.useState({});

  function writeEdges(children) {
    updateConfig({ related_config: { ...(config?.related_config || {}), children } });
  }

  function writeSources(next) {
    updateConfig({ related_config: { ...(config?.related_config || {}), sources: next } });
  }

  function addSource() {
    const first = candidates[0];
    if (!first) return;
    writeSources([...sources, {
      as: first.table,
      table: first.table,
      // Both halves of the join start empty: a source that matched everything
      // by default would quietly publish a whole table.
      where: {},
      fields: [],
    }]);
  }

  function patchSource(index, patch) {
    writeSources(sources.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  function removeSource(index) {
    writeSources(sources.filter((_, i) => i !== index));
  }

  /**
   * The conditions a source uses to choose its rows, read back from `where`.
   *
   * `where` is a plain map of column -> value, where a value is either a fixed
   * value or "@root.<field>" meaning a column of the record being shown. Zero
   * conditions is legal and means every row — which is the common case for a
   * catalogue or a notice list.
   */
  function conditionsOf(source, index) {
    // A condition being filled in lives here until it is complete. `where`
    // cannot hold it: a rule with no column yet is not a rule, and storing it
    // would mean writing a nonsense filter to the server. Without this the
    // freshly added row vanished the instant it appeared, and the builder could
    // never get past "All rows".
    if (drafts[index]) return drafts[index];

    return Object.entries(source.where || {}).map(([column, value]) => {
      const isField = typeof value === 'string' && value.startsWith('@root.');
      return {
        column,
        kind: isField ? 'field' : 'value',
        value: isField ? value.slice('@root.'.length) : (value ?? ''),
      };
    });
  }

  /** Keeps the edited list on screen, and stores the complete rules in `where`. */
  function writeConditions(index, list) {
    setDrafts(prev => ({ ...prev, [index]: list }));

    const where = {};
    for (const c of list) {
      if (!c.column) continue;                 // not a rule yet — nothing to store
      where[c.column] = c.kind === 'field' ? `@root.${c.value}` : c.value;
    }
    patchSource(index, { where });
  }

  function patchCondition(index, source, position, patch) {
    const list = [...conditionsOf(source, index)];
    list[position] = { ...list[position], ...patch };
    writeConditions(index, list);
  }

  function addCondition(index, source) {
    writeConditions(index, [...conditionsOf(source, index),
                            { column: '', kind: 'value', value: '' }]);
  }

  function removeCondition(index, source, position) {
    writeConditions(index, conditionsOf(source, index).filter((_, i) => i !== position));
  }

  function toggleSourceField(index, fieldName, on) {
    const current = sources[index].fields || [];
    patchSource(index, {
      fields: on ? [...current, fieldName] : current.filter(f => f !== fieldName),
    });
  }

  function toggleEdge(edge, on) {
    if (on) {
      writeEdges([...selected, {
        table: edge.table,
        via: edge.via,
        dir: edge.dir,
        as: edge.suggested_alias || edge.table,
        // Deliberately empty: exposure is opt-in, column by column.
        fields: [],
      }]);
    } else {
      writeEdges(selected.filter(e => e.table !== edge.table));
    }
  }

  function patchEdge(table, patch) {
    writeEdges(selected.map(e => (e.table === table ? { ...e, ...patch } : e)));
  }

  function toggleField(edge, fieldName, on) {
    const current = byTable[edge.table]?.fields || [];
    patchEdge(edge.table, {
      fields: on ? [...current, fieldName] : current.filter(f => f !== fieldName),
    });
  }

  return (
    <div>
      <div className="small text-muted mb-3">
        Choose which connected tables this page may show. Arcee matches their rows to
        the record automatically using the column shown on each row.
      </div>

      {available.length === 0 && (
        <div className="small text-muted mb-3">
          No related tables found for <code>{subject}</code>. Add a relation field on
          another table pointing at this one, and it will appear here — or match any
          table on a column below.
        </div>
      )}

      {available.map(edge => {
        const chosen = byTable[edge.table];
        const isOn = Boolean(chosen);
        const checkboxId = `edge-check-${edge.table}`;

        return (
          <div
            key={edge.table}
            data-testid={`edge-${edge.table}`}
            className="mb-3 p-2 border rounded"
            style={{ background: isOn ? '#fbfaff' : '#fff', borderColor: '#e2e8f0' }}
          >
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={checkboxId}
                checked={isOn}
                disabled={!edge.eligible}
                onChange={e => toggleEdge(edge, e.target.checked)}
              />
              <label className="form-check-label small fw-semibold" htmlFor={checkboxId}>
                {edge.display_name || edge.table}
                <span className="text-muted fw-normal">
                  {' '}— {edge.dir === 'up' ? 'linked via' : 'matched on'} <code>{edge.via}</code>
                </span>
              </label>
            </div>

            {!edge.eligible && (
              <div className="form-text text-danger ms-4">
                {edge.reason} Mark it Public to use it here.
              </div>
            )}

            {isOn && (
              <div className="ms-4 mt-2">
                <label className="form-label small fw-semibold mb-1" htmlFor={`alias-${edge.table}`}>
                  Name in template
                </label>
                <input
                  id={`alias-${edge.table}`}
                  className="form-control form-control-sm mb-2"
                  style={{ maxWidth: 260 }}
                  value={chosen.as || ''}
                  onChange={e => patchEdge(edge.table, { as: e.target.value })}
                />

                <div className="form-check mb-2">
                  <input
                    className="form-check-input" type="checkbox"
                    id={`single-${edge.table}`}
                    checked={Boolean(chosen.single)}
                    onChange={e => patchEdge(edge.table, { single: e.target.checked })}
                  />
                  <label className="form-check-label small" htmlFor={`single-${edge.table}`}>
                    Single record (not a list)
                  </label>
                  <div className="form-text">
                    Tick this when there is only ever one — an event has one organiser.
                    Leave it off for many, like an event's categories.
                  </div>
                </div>

                <div className="small fw-semibold mb-1">Columns to expose</div>
                <div className="d-flex flex-wrap gap-3 mb-2">
                  {(edge.fields || []).map(field => {
                    const fieldId = `field-${edge.table}-${field.name}`;
                    return (
                      <div className="form-check" key={field.name}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={fieldId}
                          checked={(chosen.fields || []).includes(field.name)}
                          onChange={e => toggleField(edge, field.name, e.target.checked)}
                        />
                        <label className="form-check-label small" htmlFor={fieldId}>
                          {field.name}
                        </label>
                      </div>
                    );
                  })}
                </div>

                {(chosen.fields || []).length === 0 ? (
                  <div className="form-text text-danger">
                    Choose at least one column — this table currently exposes nothing.
                  </div>
                ) : (
                  <div className="form-text">
                    Use in your HTML:{' '}
                    {chosen.single ? (
                      <code>{`{{ ${chosen.as}.${(edge.fields || [])[0]?.name || 'field'} }}`}</code>
                    ) : (
                      <code>{`<ul data-arcee-repeat="${chosen.as}">`}</code>
                    )}
                  </div>
                )}

                {loadChildEdges && (
                  <NestedRelatedBlocks
                    parent={chosen}
                    parentFrom={tableName}
                    path={chosen.as || edge.table}
                    level={1}
                    loadChildEdges={loadChildEdges}
                    onChange={children => writeEdges(selected.map(e => (
                      e.table === edge.table ? withChildren(e, children) : e
                    )))}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Any table, matched on a column ───────────────────────────────── */}
      <hr className="my-4" />

      <div className="d-flex align-items-center justify-content-between mb-2">
        <div>
          <div className="small fw-semibold">Match any table on a column</div>
          <div className="form-text mb-0">
            For a table with no relationship to <code>{subject}</code>. Pick the column
            on each side that should match. A relationship is not required — but where
            one exists, the tick-list above is usually the better choice, because a
            relationship column is indexed and an arbitrary one may not be.
          </div>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary flex-shrink-0 ms-3"
          onClick={addSource}
          disabled={candidates.length === 0}
        >
          Add a table
        </button>
      </div>

      {candidates.length === 0 && (
        <div className="form-text text-muted">
          No other public tables in this application. Mark a table Public to use it here.
        </div>
      )}

      {sources.map((source, index) => {
        const candidate = candidates.find(t => t.table === source.table) || { fields: [] };
        const conditions = conditionsOf(source, index);
        const showsEveryRow = conditions.length === 0;

        return (
          <div
            key={index}
            data-testid={`source-${index}`}
            className="mb-3 p-2 border rounded"
            style={{ background: '#fbfaff', borderColor: '#e2e8f0' }}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: 220 }}
                aria-label="Table"
                value={source.table || ''}
                onChange={e => {
                  setDrafts(prev => ({ ...prev, [index]: undefined }));
                  patchSource(index, {
                    // The columns of the old table mean nothing on the new one.
                    table: e.target.value, where: {}, fields: [],
                  });
                }}
              >
                {candidates.map(t => (
                  <option key={t.table} value={t.table}>
                    {t.display_name || t.table}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger ms-auto"
                onClick={() => removeSource(index)}
              >
                Remove
              </button>
            </div>

            {/* ── Which rows ─────────────────────────────────────────────── */}
            <div className="small fw-semibold mb-1">Which rows?</div>
            <div className="form-check">
              <input
                className="form-check-input" type="radio"
                id={`rows-all-${index}`}
                checked={showsEveryRow}
                onChange={() => writeConditions(index, [])}
              />
              <label className="form-check-label small" htmlFor={`rows-all-${index}`}>
                All rows
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input" type="radio"
                id={`rows-some-${index}`}
                checked={!showsEveryRow}
                onChange={() => addCondition(index, source)}
              />
              <label className="form-check-label small" htmlFor={`rows-some-${index}`}>
                Only rows matching conditions
              </label>
            </div>

            {!showsEveryRow && (
              <div className="ms-4 mb-2">
                {conditions.map((cond, position) => (
                  <div className="d-flex gap-2 align-items-center mb-1" key={position}>
                    <select
                      className="form-select form-select-sm" style={{ maxWidth: 180 }}
                      aria-label={`Condition ${position + 1} column`}
                      value={cond.column}
                      onChange={e => patchCondition(index, source, position,
                                                    { column: e.target.value })}
                    >
                      <option value="">— column —</option>
                      {(candidate.fields || []).map(f => (
                        <option key={f.name} value={f.name}>{f.name}</option>
                      ))}
                    </select>
                    <span className="small text-muted">is</span>
                    <select
                      className="form-select form-select-sm" style={{ maxWidth: 170 }}
                      aria-label={`Condition ${position + 1} kind`}
                      value={cond.kind}
                      onChange={e => patchCondition(index, source, position,
                                                    { kind: e.target.value, value: '' })}
                    >
                      <option value="value">a fixed value</option>
                      <option value="field">this record's field</option>
                    </select>
                    {cond.kind === 'field' ? (
                      <select
                        className="form-select form-select-sm" style={{ maxWidth: 180 }}
                        aria-label={`Condition ${position + 1} value`}
                        value={cond.value}
                        onChange={e => patchCondition(index, source, position,
                                                      { value: e.target.value })}
                      >
                        <option value="">— field —</option>
                        {recordFields.map(f => (
                          <option key={f.name} value={f.name}>{f.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="form-control form-control-sm" style={{ maxWidth: 180 }}
                        aria-label={`Condition ${position + 1} value`}
                        value={cond.value}
                        onChange={e => patchCondition(index, source, position,
                                                      { value: e.target.value })}
                      />
                    )}
                    <button
                      type="button" className="btn btn-sm btn-link text-danger"
                      onClick={() => removeCondition(index, source, position)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button" className="btn btn-sm btn-outline-secondary"
                  onClick={() => addCondition(index, source)}
                >
                  + Add condition
                </button>
              </div>
            )}

            <label className="form-label small fw-semibold mb-1" htmlFor={`source-alias-${index}`}>
              Name in template
            </label>
            <input
              id={`source-alias-${index}`}
              className="form-control form-control-sm mb-2"
              style={{ maxWidth: 260 }}
              value={source.as || ''}
              onChange={e => patchSource(index, { as: e.target.value })}
            />

            <label className="form-label small fw-semibold mb-1" htmlFor={`source-placement-${index}`}>
              Show on the list page
            </label>
            <select
              id={`source-placement-${index}`}
              className="form-select form-select-sm mb-1"
              style={{ maxWidth: 260 }}
              value={source.list_placement || 'none'}
              onChange={e => patchSource(index, { list_placement: e.target.value })}
            >
              <option value="none">Don't show</option>
              <option value="above">Above the records</option>
              <option value="below">Below the records</option>
            </select>
            <div className="form-text mb-2">
              Puts this table's data on <code>/p/{tableName || 'table'}</code> around the
              records, so the built-in card view can show it without writing a layout.
              Leave it on "Don't show" if only your own template places it.
            </div>

            <div className="form-check mb-2">
              <input
                className="form-check-input" type="checkbox"
                id={`source-single-${index}`}
                checked={Boolean(source.single)}
                onChange={e => patchSource(index, { single: e.target.checked })}
              />
              <label className="form-check-label small" htmlFor={`source-single-${index}`}>
                Single record (not a list)
              </label>
            </div>

            <div className="small fw-semibold mb-1">Columns to expose</div>
            <div className="d-flex flex-wrap gap-3 mb-2">
              {(candidate.fields || []).map(field => {
                const fieldId = `source-field-${index}-${field.name}`;
                return (
                  <div className="form-check" key={field.name}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={fieldId}
                      checked={(source.fields || []).includes(field.name)}
                      onChange={e => toggleSourceField(index, field.name, e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor={fieldId}>
                      {field.name}
                    </label>
                  </div>
                );
              })}
            </div>

            {(source.fields || []).length === 0 && (
              <div className="form-text text-danger">
                Choose at least one column — this table currently exposes nothing.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
